import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { payment, deploy } from '@/lib/api';
import { Spinner } from '@/components/ui/Spinner';
import { useStore } from '@/lib/store';

export function SuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const { resetFormData, setSessionId, setPreviews, setSelectedVersion } = useStore();

  const [status, setStatus] = useState<'loading' | 'deploying' | 'success' | 'error'>('loading');
  const [siteUrl, setSiteUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      setError('No session ID provided');
      return;
    }

    checkPaymentAndDeploy();
  }, [sessionId]);

  const checkPaymentAndDeploy = async () => {
    try {
      // Check payment status
      const paymentStatus = await payment.getStatus(sessionId!);

      if (paymentStatus.status !== 'paid') {
        // Payment might still be processing, wait and retry
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }

      setStatus('deploying');

      // Trigger deployment
      const deployResult = await deploy.trigger(
        paymentStatus.generationSessionId,
        sessionId!
      );

      setSiteUrl(deployResult.liveUrl);
      setStatus('success');

      // Clear form data
      resetFormData();
      setSessionId(null);
      setPreviews([]);
      setSelectedVersion(null);
    } catch (err) {
      console.error('Deployment error:', err);
      setError(err instanceof Error ? err.message : 'Deployment failed');
      setStatus('error');
    }
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Spinner size="lg" className="mx-auto text-primary-500" />
          <p className="mt-4 text-gray-600">Confirming your payment...</p>
        </div>
      </div>
    );
  }

  if (status === 'deploying') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="relative mx-auto w-24 h-24 mb-6">
            <div className="absolute inset-0 bg-primary-100 rounded-full animate-ping" />
            <div className="relative w-24 h-24 bg-primary-500 rounded-full flex items-center justify-center">
              <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
              </svg>
            </div>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Deploying Your Site</h2>
          <p className="text-gray-600">
            Your landing page is being deployed to our global CDN. This usually takes just a few seconds...
          </p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Deployment Issue</h2>
          <p className="text-gray-600 mb-6">
            {error || 'There was an issue deploying your site. Don\'t worry - your payment was successful and we\'ll have your site up shortly.'}
          </p>
          <div className="space-y-3">
            <Link to="/dashboard" className="btn-primary w-full">
              Go to Dashboard
            </Link>
            <p className="text-sm text-gray-500">
              Check your dashboard in a few minutes to see your live site.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-2xl mx-auto px-4">
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Your Site is Live!</h1>
          <p className="text-gray-600">
            Congratulations! Your landing page has been deployed and is ready to capture leads.
          </p>
        </div>

        <div className="card mb-6">
          <h2 className="font-semibold text-gray-900 mb-4">Your Live URL</h2>
          <div className="flex items-center space-x-3 bg-gray-50 rounded-lg p-4">
            <div className="flex-1">
              <a
                href={siteUrl || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600 hover:text-primary-700 font-medium break-all"
              >
                {siteUrl || 'Loading...'}
              </a>
            </div>
            <button
              onClick={() => {
                if (siteUrl) navigator.clipboard.writeText(siteUrl);
              }}
              className="btn-secondary text-sm"
            >
              Copy
            </button>
          </div>
        </div>

        <div className="card mb-6">
          <h2 className="font-semibold text-gray-900 mb-4">What's Next?</h2>
          <ul className="space-y-4">
            {[
              { icon: '📤', title: 'Share your page', desc: 'Post it on social media or add to your email signature' },
              { icon: '📊', title: 'Track your leads', desc: 'Visit your dashboard to see incoming leads and analytics' },
              { icon: '🔄', title: 'Iterate and improve', desc: 'Regenerate your page anytime to test new messaging' },
            ].map((item) => (
              <li key={item.title} className="flex items-start">
                <span className="text-2xl mr-4">{item.icon}</span>
                <div>
                  <h3 className="font-medium text-gray-900">{item.title}</h3>
                  <p className="text-sm text-gray-500">{item.desc}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="flex flex-col sm:flex-row gap-4">
          <a
            href={siteUrl || '#'}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary flex-1 justify-center"
          >
            View Your Site
            <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
          <Link to="/dashboard" className="btn-secondary flex-1 justify-center">
            Go to Dashboard
          </Link>
        </div>
      </div>
    </div>
  );
}
